import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from 'sonner'

type CreateEndpointInput = {
  url: string
  events: string[]
  secret?: string
  description?: string
  active?: boolean
}

type DeliveryPlatformFilter = 'webhook' | 'klaviyo' | undefined

export function useWebhookEndpoints() {
  return useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: async () => {
      return api.webhooks.getEndpoints()
    },
  })
}

export function useCreateWebhookEndpoint() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateEndpointInput) => {
      return api.webhooks.createEndpoint(input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] })
      toast.success('Webhook endpoint criado com sucesso')
    },
    onError: () => {
      toast.error('Erro ao criar webhook endpoint')
    },
  })
}

export function useDeleteWebhookEndpoint() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.webhooks.deleteEndpoint(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] })
      toast.success('Webhook endpoint removido')
    },
    onError: () => {
      toast.error('Erro ao remover webhook endpoint')
    },
  })
}

export function useToggleWebhookEndpoint() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      return api.webhooks.toggleEndpoint(id, active)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] })
      toast.success('Status do endpoint atualizado')
    },
    onError: () => {
      toast.error('Erro ao atualizar status do endpoint')
    },
  })
}

export function usePingWebhookEndpoint() {
  return useMutation({
    mutationFn: async (id: string) => {
      return api.webhooks.pingEndpoint(id)
    },
    onSuccess: () => {
      toast.success('Evento de teste enviado com sucesso!')
    },
    onError: () => {
      toast.error('Erro ao enviar evento de teste')
    },
  })
}

export function useWebhookDeliveries(limit = 20, offset = 0, platform?: DeliveryPlatformFilter) {
  return useQuery({
    queryKey: ['webhook-deliveries', limit, offset, platform],
    queryFn: async () => {
      return api.webhooks.getDeliveries(limit, offset, platform)
    },
    // Refresh every 30s as logs are frequent
    refetchInterval: 30000,
  })
}
